import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  Calendar,
  Clock,
  Sparkles,
  Trophy,
  Award,
  Users,
  Dna,
  FlaskConical,
  GitMerge,
  ExternalLink,
  MessageSquare,
  Mail,
  Phone,
  CheckCircle2,
  Copy,
  Globe,
  Atom,
  Terminal,
  ShieldCheck,
  Brain,
  Star,
  X,
  Download
} from 'lucide-react';
import Footer from '../components/footer/Footer';
import SEOHead from '../seo/SEOHead';
import { getSEOData } from '../seo/seoConfig';
import { getEventSchema } from '../seo/structuredData';

function RnaIcon({ size = 24, color = 'currentColor', strokeWidth = 2, className = '' }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      
      <path d="M7 21V10a5 5 0 0 1 10 0v11" />
      
      <line x1="7" y1="13.5" x2="17" y2="13.5" />
      <line x1="7" y1="17" x2="17" y2="17" />
      
      <path d="M4 21h3" />
      <path d="M17 21h3" />
      
      <path d="M12 5v2.5" />
    </svg>
  );
}

function SpotlightCard({ children, className = '', style = {}, onClick }) {
  const cardRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: -1000, y: -1000, isHovered: false });

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      isHovered: true
    });
  };

  const handleMouseLeave = () => {
    setMousePos({ x: -1000, y: -1000, isHovered: false });
  };

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      className={className}
      style={{
        position: 'relative',
        borderRadius: '24px',
        backgroundColor: '#ffffff',
        border: '1px solid rgba(15, 23, 42, 0.08)',
        boxShadow: '0 4px 20px -2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02)',
        transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.3s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.3s ease',
        overflow: 'hidden',
        ...style
      }}
    >
      {mousePos.isHovered && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: `radial-gradient(480px circle at ${mousePos.x}px ${mousePos.y}px, rgba(124, 58, 237, 0.08), transparent 70%)`,
            zIndex: 0,
            borderRadius: 'inherit'
          }}
        />
      )}
      <div style={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {children}
      </div>
    </div>
  );
}

const sectionVariant = {
  hidden: { opacity: 0, y: 32 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.09, delayChildren: 0.06 }
  }
};

const cardVariant = {
  hidden: { opacity: 0, y: 22, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] }
  }
};

export default function SciSprint2026() {
  const [isMobile, setIsMobile] = useState(false);
  const [showPosterModal, setShowPosterModal] = useState(false);

  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isLive, setIsLive] = useState(false);
  const [overallCompleted, setOverallCompleted] = useState(false);

  const [sprintCountdowns, setSprintCountdowns] = useState([
    { days: 0, hours: 0, minutes: 0, seconds: 0, status: 'upcoming' },
    { days: 0, hours: 0, minutes: 0, seconds: 0, status: 'upcoming' },
    { days: 0, hours: 0, minutes: 0, seconds: 0, status: 'upcoming' },
    { days: 0, hours: 0, minutes: 0, seconds: 0, status: 'upcoming' }
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);

    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);

    const kickoffTargetDate = new Date('November 9, 2026 09:00:00 GMT+0530').getTime();
    const seriesEndDate = new Date('December 6, 2026 23:59:59 GMT+0530').getTime();

    const sprintBenchmarks = [
      { start: new Date('November 9, 2026 09:00:00 GMT+0530').getTime(), end: new Date('November 15, 2026 23:59:59 GMT+0530').getTime() },
      { start: new Date('November 16, 2026 09:00:00 GMT+0530').getTime(), end: new Date('November 22, 2026 23:59:59 GMT+0530').getTime() },
      { start: new Date('November 23, 2026 09:00:00 GMT+0530').getTime(), end: new Date('November 29, 2026 23:59:59 GMT+0530').getTime() },
      { start: new Date('November 30, 2026 09:00:00 GMT+0530').getTime(), end: new Date('December 6, 2026 23:59:59 GMT+0530').getTime() }
    ];

    const updateCountdown = () => {
      const now = new Date().getTime();
      const difference = kickoffTargetDate - now;

      if (now > seriesEndDate) {
        
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        setIsLive(false);
        setOverallCompleted(true);
      } else if (difference > 0) {
        
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        });
        setIsLive(false);
        setOverallCompleted(false);
      } else {
        
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        setIsLive(true);
        setOverallCompleted(false);
      }

      const updatedSprints = sprintBenchmarks.map(({ start, end }) => {
        if (now < start) {
          const diff = start - now;
          return {
            status: 'upcoming',
            days: Math.floor(diff / (1000 * 60 * 60 * 24)),
            hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
            minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
            seconds: Math.floor((diff % (1000 * 60)) / 1000)
          };
        } else if (now <= end) {
          const diff = end - now;
          return {
            status: 'live',
            days: Math.floor(diff / (1000 * 60 * 60 * 24)),
            hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
            minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
            seconds: Math.floor((diff % (1000 * 60)) / 1000)
          };
        } else {
          return {
            status: 'completed',
            days: 0,
            hours: 0,
            minutes: 0,
            seconds: 0
          };
        }
      });
      setSprintCountdowns(updatedSprints);
    };

    updateCountdown();
    const timerInterval = setInterval(updateCountdown, 1000);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearInterval(timerInterval);
    };
  }, []);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText('contact@chembioai.org');
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2200);
  };

  const handleCopyPhone = () => {
    navigator.clipboard.writeText('+918919820691');
    setCopiedPhone(true);
    setTimeout(() => setCopiedPhone(false), 2200);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText('https://forms.gle/Px6ncuHhr4n4ZMJy8');
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2200);
  };

  const seoData = getSEOData('/scisprint-2026') || {
    title: 'SciSprint 2026 | Agent Scientist Hackathon Series | Boltzmann Labs & ChemBio AI',
    description: 'Four Weeks. Four Scientific Domains. Twelve Real World Research Challenges. One Scientific Revolution. Join SciSprint 2026 powered by Agent Scientist, developed by Boltzmann Labs with ChemBio AI.',
    keywords: 'SciSprint 2026, Agent Scientist, Boltzmann Labs, ChemBio AI, scientific hackathon, protein engineering, drug discovery, retrosynthesis, RNA design',
    image: '/scisprint2026_poster.jpg',
    url: 'https://boltzmann.co/scisprint-2026'
  };

  const eventSchema = getEventSchema({
    name: 'SciSprint 2026: Agent Scientist Hackathon Series',
    description: 'Four Weeks. Four Scientific Domains. Twelve Real World Research Challenges. One Scientific Revolution. Multi-domain scientific hackathon series powered by Agent Scientist, developed by Boltzmann Labs in collaboration with ChemBio AI.',
    startDate: '2026-11-09T09:00:00+05:30',
    endDate: '2026-12-06T23:59:59+05:30',
    url: 'https://boltzmann.co/scisprint-2026',
    image: '/scisprint2026_poster.jpg'
  });

  const schedule = [
    {
      num: '01',
      title: 'Protein Forge',
      dates: '9–15 November 2026',
      domain: 'Protein Engineering',
      desc: 'Explore AI powered protein engineering, protein structure analysis and computational protein design.',
      accent: '#7c3aed',
      accentLight: '#f5f3ff',
      icon: <Dna size={24} color="#7c3aed" />
    },
    {
      num: '02',
      title: 'Molecule Masters',
      dates: '16–22 November 2026',
      domain: 'Small Molecule Design',
      desc: 'Discover AI driven small molecule design, computational drug discovery and molecular optimisation.',
      accent: '#0284c7',
      accentLight: '#f0f9ff',
      icon: <FlaskConical size={24} color="#0284c7" />
    },
    {
      num: '03',
      title: 'Synthesis X',
      dates: '23–29 November 2026',
      domain: 'Synthetic Chemistry',
      desc: 'Explore synthetic chemistry, chemical reaction prediction and AI assisted retrosynthesis.',
      accent: '#059669',
      accentLight: '#ecfdf5',
      icon: <GitMerge size={24} color="#059669" />
    },
    {
      num: '04',
      title: 'RNA Recode',
      dates: '30 November – 6 December 2026',
      domain: 'RNA Design',
      desc: 'Discover computational RNA design, sequence optimisation and AI driven RNA research.',
      accent: '#e11d48',
      accentLight: '#fff1f2',
      icon: <RnaIcon size={24} color="#e11d48" />
    }
  ];

  const participants = [
    {
      category: 'Academics',
      role: 'Undergraduate & Postgraduate Students',
      desc: 'Gain hands-on experience with cutting-edge AI scientific workflows and computational discovery tools.',
      focus: 'AI Scientific Workflows & Connected Tools',
      icon: <Users size={22} color="#7c3aed" />,
      accent: '#7c3aed',
      accentLight: '#f5f3ff'
    },
    {
      category: 'Research',
      role: 'PhD Scholars & Researchers',
      desc: 'Accelerate empirical inquiry with autonomous agent workflows and frontier research skills.',
      focus: 'Autonomous Agents & Scientific Inquiry',
      icon: <Atom size={22} color="#0284c7" />,
      accent: '#0284c7',
      accentLight: '#f0f9ff'
    },
    {
      category: 'Genomics',
      role: 'Bioinformaticians & Scientists',
      desc: 'Model biological macromolecular sequences, protein folds, and structural conformational switches.',
      focus: 'Sequence Modeling & Structure Analysis',
      icon: <Dna size={22} color="#059669" />,
      accent: '#059669',
      accentLight: '#ecfdf5'
    },
    {
      category: 'Chemistry',
      role: 'Computational Chemists',
      desc: 'Explore generative small molecule optimization, multi-step retrosynthesis, and lead screening.',
      focus: 'Small Molecules & Reaction Prediction',
      icon: <FlaskConical size={22} color="#d97706" />,
      accent: '#d97706',
      accentLight: '#fffbeb'
    },
    {
      category: 'Engineering',
      role: 'AI Developers & Engineers',
      desc: 'Harness foundation models, neural modeling, and scientific agents in applied research tracks.',
      focus: 'Foundation Models & Scientific Agents',
      icon: <Brain size={22} color="#e11d48" />,
      accent: '#e11d48',
      accentLight: '#fff1f2'
    },
    {
      category: 'Innovation',
      role: 'AI & Life Sciences Enthusiasts',
      desc: 'Anyone passionate about applying artificial intelligence to scientific research and innovation.',
      focus: 'Cross-Disciplinary AI Discovery',
      icon: <Sparkles size={22} color="#4F1985" />,
      accent: '#4F1985',
      accentLight: '#faf8fe'
    }
  ];

  const whyParticipate = [
    {
      num: '01',
      title: 'Work on Real-World Challenges',
      desc: 'Work on real world scientific research challenges framed across four cutting-edge domains.',
      accent: '#7c3aed',
      accentLight: '#f5f3ff',
      icon: <Atom size={22} color="#7c3aed" />
    },
    {
      num: '02',
      title: 'Hands-on Agent Scientist Experience',
      desc: 'Gain hands-on experience with Agent Scientist, the unified open source AI research ecosystem.',
      accent: '#0284c7',
      accentLight: '#f0f9ff',
      icon: <Sparkles size={22} color="#0284c7" />
    },
    {
      num: '03',
      title: 'Explore AI Powered Workflows',
      desc: 'Explore AI powered scientific workflows that connect questions to tool supported discovery.',
      accent: '#059669',
      accentLight: '#ecfdf5',
      icon: <Terminal size={22} color="#059669" />
    },
    {
      num: '04',
      title: 'Connect with Innovators',
      desc: 'Connect with researchers and innovators collaborating across global computational science.',
      accent: '#e11d48',
      accentLight: '#fff1f2',
      icon: <Globe size={22} color="#e11d48" />
    },
    {
      num: '05',
      title: 'Showcase Your Solutions',
      desc: 'Showcase your ideas and scientific solutions to the broader life sciences research network.',
      accent: '#d97706',
      accentLight: '#fffbeb',
      icon: <Award size={22} color="#d97706" />
    },
    {
      num: '06',
      title: 'ChemBio AI Research Community',
      desc: 'Become part of the ChemBio AI research community and collaborate on future scientific activities.',
      accent: '#4F1985',
      accentLight: '#faf8fe',
      icon: <MessageSquare size={22} color="#4F1985" />
    }
  ];

  return (
    <div
      style={{
        backgroundColor: '#ffffff',
        minHeight: '100vh',
        color: '#0f172a',
        fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif",
        overflowX: 'clip',
        position: 'relative'
      }}
    >
      <SEOHead {...seoData} structuredData={eventSchema} />

      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundImage: 'radial-gradient(circle, rgba(148, 163, 184, 0.18) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
          pointerEvents: 'none',
          zIndex: 0
        }}
      />

      <div
        style={{
          position: 'absolute',
          top: '-140px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '900px',
          height: '500px',
          background: 'radial-gradient(ellipse at center, rgba(79, 25, 133, 0.08) 0%, rgba(144, 71, 255, 0.02) 50%, transparent 75%)',
          filter: 'blur(70px)',
          pointerEvents: 'none',
          zIndex: 0
        }}
      />

      <section
        style={{
          position: 'relative',
          paddingTop: isMobile ? '92px' : '108px',
          paddingBottom: isMobile ? '36px' : '56px',
          maxWidth: '1360px',
          margin: '0 auto',
          paddingLeft: isMobile ? '16px' : '32px',
          paddingRight: isMobile ? '16px' : '32px',
          zIndex: 1
        }}
      >
        
        <div
          style={{
            textAlign: 'center',
            maxWidth: '1080px',
            margin: '0 auto',
            marginBottom: isMobile ? '28px' : '38px'
          }}
        >
          <h1
            style={{
              fontFamily: "'Space Grotesk', -apple-system, sans-serif",
              fontSize: 'clamp(3.1rem, 7.2vw, 5.8rem)',
              fontWeight: 900,
              lineHeight: 1.02,
              letterSpacing: '-0.04em',
              color: '#0f172a',
              margin: '0 0 12px'
            }}
          >
            SciSprint{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #170d2b 0%, #4F1985 45%, #9047ff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                display: 'inline-block'
              }}
            >
              2026
            </span>
          </h1>

          <div
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: 'clamp(1.25rem, 2.4vw, 1.95rem)',
              fontWeight: 750,
              color: '#4F1985',
              letterSpacing: '-0.02em',
              marginBottom: '16px'
            }}
          >
            Agent Scientist Hackathon Series
          </div>

          <p
            style={{
              fontSize: 'clamp(1.05rem, 1.55vw, 1.25rem)',
              fontWeight: 700,
              color: '#0f172a',
              lineHeight: 1.45,
              maxWidth: '920px',
              margin: '0 auto 14px',
              letterSpacing: '-0.015em'
            }}
          >
            Four Weeks. Four Scientific Domains. Twelve Real World Research Challenges. One Scientific Revolution.
          </p>

          <p
            style={{
              fontSize: 'clamp(0.95rem, 1.2vw, 1.08rem)',
              color: '#475569',
              lineHeight: 1.7,
              maxWidth: '860px',
              margin: '0 auto',
              fontWeight: 450
            }}
          >
            Get ready to explore the future of AI powered scientific research with <strong>SciSprint 2026</strong>, a multi domain scientific hackathon series designed for students, research scholars, scientists, bioinformaticians, computational chemists and AI enthusiasts.
          </p>
        </div>

        <div
          style={{
            maxWidth: '1160px',
            margin: '0 auto',
            marginBottom: isMobile ? '32px' : '44px'
          }}
        >
          <motion.div
            whileHover={{ scale: 1.008 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            style={{
              position: 'relative',
              borderRadius: isMobile ? '22px' : '32px',
              backgroundColor: '#0c0717',
              border: '1px solid rgba(79, 25, 133, 0.18)',
              boxShadow: '0 25px 70px -15px rgba(79, 25, 133, 0.22), 0 8px 30px rgba(15, 23, 42, 0.08)',
              overflow: 'hidden',
              cursor: 'pointer'
            }}
            onClick={() => setShowPosterModal(true)}
            title="Click to view official poster in full screen"
          >
            <div
              style={{
                position: 'relative',
                aspectRatio: isMobile ? '4/3' : '16/9',
                backgroundColor: '#0c0717',
                overflow: 'hidden'
              }}
            >
              <img
                src="/scisprint2026_poster.jpg"
                alt="SciSprint 2026 Official Key Visual"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block'
                }}
              />
            </div>
          </motion.div>
        </div>

        <div
          style={{
            maxWidth: '1160px',
            margin: '0 auto',
            backgroundColor: '#ffffff',
            borderRadius: '26px',
            border: '1px solid rgba(15, 23, 42, 0.08)',
            boxShadow: '0 12px 36px -8px rgba(15, 23, 42, 0.06), 0 2px 6px rgba(0, 0, 0, 0.02)',
            padding: isMobile ? '20px 16px' : '24px 32px',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '20px'
          }}
        >
          
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '20px' }}>
            <div
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '14px',
                backgroundColor: '#faf8fe',
                border: '1px solid rgba(79, 25, 133, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <Clock size={20} color="#4F1985" />
            </div>

            <div>
              <div
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '11px',
                  fontWeight: 800,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  marginBottom: '2px'
                }}
              >
                {overallCompleted
                  ? 'SERIES STATUS: EVENT COMPLETED'
                  : isLive
                  ? 'SciSprint is Live! (9 Nov – 6 Dec)'
                  : 'KICKOFF COUNTDOWN (9 NOV 2026):'}
              </div>

              <div
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: isMobile ? '1.4rem' : '1.75rem',
                  fontWeight: 850,
                  color: '#0f172a',
                  letterSpacing: '-0.03em',
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1.1
                }}
              >
                {overallCompleted ? (
                  <span style={{ fontSize: isMobile ? '1.15rem' : '1.35rem', color: '#64748b' }}>
                    Winners: 8 Dec 2026
                  </span>
                ) : (
                  <>
                    <span>{timeLeft.days.toString().padStart(2, '0')}</span>
                    <span style={{ color: '#94a3b8', margin: '0 4px' }}>d</span>
                    <span>{timeLeft.hours.toString().padStart(2, '0')}</span>
                    <span style={{ color: '#94a3b8', margin: '0 4px' }}>h</span>
                    <span>{timeLeft.minutes.toString().padStart(2, '0')}</span>
                    <span style={{ color: '#94a3b8', margin: '0 4px' }}>m</span>
                    <span style={{ color: '#4F1985' }}>{timeLeft.seconds.toString().padStart(2, '0')}</span>
                    <span style={{ color: '#94a3b8', marginLeft: '4px' }}>s</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'wrap',
              width: isMobile ? '100%' : 'auto'
            }}
          >
            <a
              href={overallCompleted ? "#completed" : "https://forms.gle/Px6ncuHhr4n4ZMJy8"}
              onClick={(e) => { if (overallCompleted) e.preventDefault(); }}
              target={overallCompleted ? undefined : "_blank"}
              rel="noopener noreferrer"
              className={overallCompleted ? undefined : "glossy-btn-primary glossy-btn-sheen"}
              style={{
                flex: isMobile ? '1 1 100%' : '0 0 auto',
                padding: '14px 28px',
                borderRadius: '9999px',
                fontSize: '14.5px',
                fontWeight: 750,
                fontFamily: "'Space Grotesk', sans-serif",
                textDecoration: 'none',
                color: '#ffffff',
                backgroundColor: overallCompleted ? '#94a3b8' : undefined,
                cursor: overallCompleted ? 'not-allowed' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                opacity: overallCompleted ? 0.75 : 1
              }}
            >
              <span>{overallCompleted ? 'Event Completed' : 'Register Free Now'}</span>
              {!overallCompleted && <ArrowRight size={16} />}
            </a>

            <a
              href="https://discord.gg/rXTkjn2HhH"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                flex: isMobile ? '1 1 auto' : '0 0 auto',
                padding: '13px 22px',
                borderRadius: '9999px',
                fontSize: '14px',
                fontWeight: 750,
                fontFamily: "'Space Grotesk', sans-serif",
                textDecoration: 'none',
                color: '#5865F2',
                backgroundColor: '#ffffff',
                border: '1px solid rgba(88, 101, 242, 0.3)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <MessageSquare size={16} color="#5865F2" />
              <span>Join Discord</span>
              <ExternalLink size={13} color="#94a3b8" />
            </a>

            <button
              type="button"
              onClick={handleCopyLink}
              style={{
                padding: '13px 18px',
                borderRadius: '9999px',
                fontSize: '13px',
                fontWeight: 650,
                fontFamily: "'Space Grotesk', sans-serif",
                backgroundColor: '#faf8fe',
                border: '1px solid rgba(79, 25, 133, 0.15)',
                color: '#4F1985',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
              title="Copy Registration Link"
            >
              {copiedLink ? <CheckCircle2 size={15} color="#10b981" /> : <Copy size={15} />}
              <span>{copiedLink ? 'Copied' : 'Share'}</span>
            </button>
          </div>
        </div>

        <div
          style={{
            maxWidth: '1160px',
            margin: '24px auto 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '10px',
            padding: '0 16px'
          }}
        >
          {[
            {
              icon: <Calendar size={14} color="#7c3aed" />,
              text: '9 Nov – 6 Dec 2026',
              bg: 'rgba(124, 58, 237, 0.06)',
              border: 'rgba(124, 58, 237, 0.16)',
              color: '#5b21b6'
            },
            {
              icon: <Trophy size={14} color="#d97706" />,
              text: 'INR 8,000 Rewards Pool',
              bg: 'rgba(217, 119, 6, 0.08)',
              border: 'rgba(217, 119, 6, 0.22)',
              color: '#b45309'
            },
            {
              icon: <Award size={14} color="#4F1985" />,
              text: 'Certificates for All Participants',
              bg: 'rgba(79, 25, 133, 0.06)',
              border: 'rgba(79, 25, 133, 0.16)',
              color: '#4F1985'
            },
            {
              icon: <Globe size={14} color="#0284c7" />,
              text: '100% Online • Global',
              bg: 'rgba(2, 132, 199, 0.06)',
              border: 'rgba(2, 132, 199, 0.18)',
              color: '#0369a1'
            },
            {
              icon: <Sparkles size={14} color="#059669" />,
              text: 'Grand Winners: 8 Dec 2026',
              bg: 'rgba(5, 150, 105, 0.06)',
              border: 'rgba(5, 150, 105, 0.18)',
              color: '#047857'
            }
          ].map((pill) => (
            <div
              key={pill.text}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '7px',
                padding: '7px 15px',
                borderRadius: '9999px',
                backgroundColor: pill.bg,
                border: `1px solid ${pill.border}`,
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.02)',
                fontSize: '12.5px',
                fontFamily: "'Space Grotesk', -apple-system, sans-serif",
                fontWeight: 650,
                color: pill.color,
                letterSpacing: '-0.01em'
              }}
            >
              {pill.icon}
              <span>{pill.text}</span>
            </div>
          ))}
        </div>
      </section>

      <motion.section
        variants={sectionVariant}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-60px' }}
        style={{
          position: 'relative',
          padding: isMobile ? '56px 16px' : '88px 32px',
          maxWidth: '1240px',
          margin: '0 auto',
          zIndex: 1
        }}
      >
        <SpotlightCard
          style={{
            padding: isMobile ? '36px 20px' : '56px 56px'
          }}
        >
          <div style={{ textAlign: 'center', maxWidth: '900px', margin: '0 auto 36px' }}>
            <h2
              style={{
                fontFamily: "'Space Grotesk', -apple-system, sans-serif",
                fontSize: isMobile ? '2.4rem' : '3.4rem',
                fontWeight: 850,
                letterSpacing: '-0.035em',
                color: '#0f172a',
                lineHeight: 1.12,
                margin: '0 0 20px'
              }}
            >
              What is an{' '}
              <span
                style={{
                  background: 'linear-gradient(135deg, #170d2b 0%, #4F1985 50%, #9047ff 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                Agent Scientist
              </span>
              ?
            </h2>

            <p
              style={{
                fontSize: isMobile ? '16.5px' : '18.5px',
                color: '#334155',
                lineHeight: 1.76,
                margin: '0 auto',
                fontWeight: 450
              }}
            >
              <strong>Agent Scientist</strong> is an open source AI agent system developed by <strong>Boltzmann Labs</strong> to accelerate scientific research. It brings AI assistance, scientific research skills, connected computational tools and research workflows into a unified workspace, helping researchers move from scientific questions to structured, tool supported discovery.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
              gap: '16px',
              paddingTop: '28px',
              borderTop: '1px solid rgba(15, 23, 42, 0.06)'
            }}
          >
            {[
              {
                step: '01',
                title: 'Scientific Questions',
                sub: 'Hypothesis Formulation',
                accent: '#7c3aed',
                accentLight: '#f5f3ff',
                icon: <Atom size={20} color="#7c3aed" />
              },
              {
                step: '02',
                title: 'AI Assistance',
                sub: 'Autonomous Skills',
                accent: '#0284c7',
                accentLight: '#f0f9ff',
                icon: <Brain size={20} color="#0284c7" />
              },
              {
                step: '03',
                title: 'Connected Tools',
                sub: 'Unified Workspace',
                accent: '#059669',
                accentLight: '#ecfdf5',
                icon: <Terminal size={20} color="#059669" />
              },
              {
                step: '04',
                title: 'Structured Discovery',
                sub: 'Validated Outcomes',
                accent: '#e11d48',
                accentLight: '#fff1f2',
                icon: <CheckCircle2 size={20} color="#e11d48" />
              }
            ].map((node, i) => (
              <motion.div
                key={i}
                variants={cardVariant}
                whileHover={{ y: -5 }}
                transition={{ type: 'spring', stiffness: 350, damping: 25 }}
                style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '18px',
                  padding: '22px 20px',
                  border: `1px solid ${node.accent}22`,
                  boxShadow: `0 4px 16px -2px ${node.accent}12, 0 1px 3px rgba(0,0,0,0.02)`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                  position: 'relative'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '12px',
                      backgroundColor: node.accentLight,
                      border: `1px solid ${node.accent}25`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {node.icon}
                  </div>
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: '11px',
                      fontWeight: 850,
                      color: node.accent,
                      backgroundColor: `${node.accent}12`,
                      padding: '3px 10px',
                      borderRadius: '9999px',
                      letterSpacing: '0.04em'
                    }}
                  >
                    Phase {node.step}
                  </span>
                </div>
                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '15.5px', fontWeight: 800, color: '#0f172a' }}>
                  {node.title}
                </div>
                <div style={{ fontSize: '12.5px', color: '#64748b', lineHeight: 1.5 }}>
                  {node.sub}
                </div>
              </motion.div>
            ))}
          </div>
        </SpotlightCard>
      </motion.section>

      <motion.section
        id="schedule"
        variants={sectionVariant}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-60px' }}
        style={{
          position: 'relative',
          padding: isMobile ? '56px 16px' : '88px 32px',
          maxWidth: '1360px',
          margin: '0 auto',
          zIndex: 1
        }}
      >
        <div style={{ textAlign: 'center', maxWidth: '880px', margin: '0 auto 48px' }}>
          <h2
            style={{
              fontFamily: "'Space Grotesk', -apple-system, sans-serif",
              fontSize: isMobile ? '2.3rem' : '3.3rem',
              fontWeight: 850,
              letterSpacing: '-0.035em',
              color: '#0f172a',
              margin: '0 0 16px'
            }}
          >
            Four Hackathons. Four Scientific Domains.{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #170d2b 0%, #4F1985 50%, #9047ff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Twelve Research Challenges.
            </span>
          </h2>

          <p style={{ fontSize: '1.08rem', color: '#64748b', lineHeight: 1.7, margin: 0 }}>
            Across four consecutive weeks, participants will explore four exciting scientific domains, work on practical research challenges, experiment with AI powered scientific tools and showcase their solutions.
          </p>
        </div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-40px' }}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 250px), 1fr))',
            gap: '20px',
            marginBottom: '32px'
          }}
        >
          {schedule.map((w, idx) => (
            <motion.div
              key={idx}
              variants={cardVariant}
              whileHover={{ y: -8 }}
              transition={{ type: 'spring', stiffness: 350, damping: 25 }}
            >
              <SpotlightCard
                style={{
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                  padding: '30px 24px',
                  backgroundColor: '#ffffff',
                  boxShadow: '0 4px 24px -2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  height: '100%'
                }}
              >
                <div>
                  
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '18px'
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: '11px',
                        fontWeight: 850,
                        color: w.accent,
                        backgroundColor: `${w.accent}12`,
                        border: `1px solid ${w.accent}22`,
                        padding: '4px 12px',
                        borderRadius: '9999px',
                        letterSpacing: '0.04em'
                      }}
                    >
                      Sprint {w.num}
                    </span>
                    <div
                      style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '14px',
                        backgroundColor: w.accentLight,
                        border: `1px solid ${w.accent}25`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 4px 12px -2px ${w.accent}18`
                      }}
                    >
                      {w.icon}
                    </div>
                  </div>

                  <h3
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: '1.45rem',
                      fontWeight: 850,
                      color: '#0f172a',
                      lineHeight: 1.25,
                      margin: '0 0 14px'
                    }}
                  >
                    {w.title}
                  </h3>

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      marginBottom: '16px'
                    }}
                  >
                    
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '7px',
                        padding: '5px 10px',
                        borderRadius: '10px',
                        backgroundColor: w.accentLight,
                        border: `1.5px solid ${w.accent}25`,
                        boxShadow: `0 2px 8px -2px ${w.accent}15`,
                        width: 'fit-content',
                        maxWidth: '100%'
                      }}
                    >
                      <div
                        style={{
                          width: '18px',
                          height: '18px',
                          borderRadius: '4px',
                          backgroundColor: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                        }}
                      >
                        <Calendar size={10.5} color={w.accent} />
                      </div>
                      <span
                        style={{
                          fontFamily: "'Space Grotesk', sans-serif",
                          fontSize: '11.5px',
                          fontWeight: 800,
                          color: w.accent,
                          letterSpacing: '-0.02em',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {w.dates}
                      </span>
                    </div>

                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '6px 11px',
                        borderRadius: '10px',
                        backgroundColor: sprintCountdowns[idx]?.status === 'completed' ? '#f8fafc' : '#ffffff',
                        border: sprintCountdowns[idx]?.status === 'completed'
                          ? '1px solid #e2e8f0'
                          : `1px solid ${w.accent}30`,
                        boxShadow: sprintCountdowns[idx]?.status === 'completed'
                          ? 'none'
                          : `0 2px 10px -2px ${w.accent}14`,
                        width: 'fit-content',
                        maxWidth: '100%',
                        opacity: sprintCountdowns[idx]?.status === 'completed' ? 0.78 : 1
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span
                          style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            backgroundColor: sprintCountdowns[idx]?.status === 'completed'
                              ? '#94a3b8'
                              : sprintCountdowns[idx]?.status === 'live'
                              ? '#10b981'
                              : w.accent,
                            display: 'inline-block',
                            boxShadow: sprintCountdowns[idx]?.status === 'completed'
                              ? 'none'
                              : `0 0 6px ${sprintCountdowns[idx]?.status === 'live' ? '#10b981' : w.accent}`
                          }}
                        />
                        <span
                          style={{
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: '9.5px',
                            fontWeight: 800,
                            color: sprintCountdowns[idx]?.status === 'completed' ? '#94a3b8' : '#64748b',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em'
                          }}
                        >
                          {sprintCountdowns[idx]?.status === 'completed'
                            ? 'STATUS:'
                            : sprintCountdowns[idx]?.status === 'live'
                            ? 'LIVE NOW:'
                            : 'KICKOFF IN:'}
                        </span>
                      </div>
                      <span
                        style={{
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: '11px',
                          fontWeight: 850,
                          color: sprintCountdowns[idx]?.status === 'completed' ? '#64748b' : '#0f172a',
                          letterSpacing: '-0.02em',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {sprintCountdowns[idx]?.status === 'completed' ? (
                          <span>Sprint Completed ✓</span>
                        ) : (
                          <>
                            {sprintCountdowns[idx]?.days ?? 0}d{' '}
                            <span style={{ color: w.accent }}>
                              {String(sprintCountdowns[idx]?.hours ?? 0).padStart(2, '0')}h
                            </span>{' '}
                            {String(sprintCountdowns[idx]?.minutes ?? 0).padStart(2, '0')}m{' '}
                            <span style={{ color: w.accent }}>
                              {String(sprintCountdowns[idx]?.seconds ?? 0).padStart(2, '0')}s
                            </span>
                          </>
                        )}
                      </span>
                    </div>
                  </div>

                  <p style={{ fontSize: '13.5px', color: '#475569', lineHeight: 1.65, margin: 0 }}>
                    {w.desc}
                  </p>
                </div>

                <div
                  style={{
                    marginTop: '22px',
                    paddingTop: '16px',
                    borderTop: '1px solid rgba(15, 23, 42, 0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '7px',
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: '12px',
                      fontWeight: 750,
                      color: w.accent,
                      backgroundColor: `${w.accent}10`,
                      padding: '5px 12px',
                      borderRadius: '8px'
                    }}
                  >
                    <span
                      style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        backgroundColor: w.accent,
                        boxShadow: `0 0 8px ${w.accent}`
                      }}
                    />
                    {w.domain}
                  </span>

                  <span
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: '11px',
                      fontWeight: 800,
                      color: '#94a3b8'
                    }}
                  >
                    Week {w.num}
                  </span>
                </div>
              </SpotlightCard>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          style={{
            background: 'linear-gradient(135deg, #fffdfa 0%, #fffbeb 55%, #fef3c7 100%)',
            borderRadius: '24px',
            border: '1.5px solid rgba(245, 158, 11, 0.45)',
            boxShadow: '0 12px 36px -6px rgba(245, 158, 11, 0.16), 0 2px 8px rgba(0,0,0,0.02)',
            padding: isMobile ? '22px 18px' : '28px 36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '20px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flex: '1 1 500px' }}>
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '18px',
                backgroundColor: '#ffffff',
                border: '1.5px solid #fde68a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: '0 4px 16px rgba(245, 158, 11, 0.18)'
              }}
            >
              <Trophy size={28} color="#d97706" />
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '6px' }}>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '10.5px',
                    fontWeight: 800,
                    color: '#b45309',
                    backgroundColor: 'rgba(245, 158, 11, 0.15)',
                    padding: '3px 10px',
                    borderRadius: '9999px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em'
                  }}
                >
                  Culmination Milestone
                </span>

                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '12px',
                    fontWeight: 850,
                    color: '#92400e',
                    backgroundColor: '#ffffff',
                    border: '1px solid #fcd34d',
                    padding: '3px 12px',
                    borderRadius: '9999px',
                    boxShadow: '0 1px 4px rgba(245, 158, 11, 0.12)'
                  }}
                >
                  <Calendar size={13} color="#d97706" />
                  <span>8 DECEMBER 2026</span>
                </span>
              </div>

              <div
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: isMobile ? '1.3rem' : '1.6rem',
                  fontWeight: 850,
                  color: '#0f172a',
                  lineHeight: 1.2
                }}
              >
                Grand Winner Announcements
              </div>

              <div style={{ fontSize: '13.5px', color: '#475569', marginTop: '4px' }}>
                Honoring outstanding scientific breakthroughs, solutions, and community contributions.
              </div>
            </div>
          </div>

          <a
            href="https://discord.gg/rXTkjn2HhH"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: '13px 26px',
              borderRadius: '9999px',
              backgroundColor: '#0f172a',
              color: '#ffffff',
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: '14px',
              fontWeight: 750,
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 4px 16px rgba(15, 23, 42, 0.25)',
              transition: 'all 0.2s ease'
            }}
          >
            <span>Follow Announcements</span>
            <ArrowRight size={15} />
          </a>
        </motion.div>
      </motion.section>

      <motion.section
        variants={sectionVariant}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-60px' }}
        style={{
          position: 'relative',
          padding: isMobile ? '56px 16px' : '88px 32px',
          maxWidth: '1360px',
          margin: '0 auto',
          zIndex: 1
        }}
      >
        <div style={{ textAlign: 'center', maxWidth: '820px', margin: '0 auto 44px' }}>
          <h2
            style={{
              fontFamily: "'Space Grotesk', -apple-system, sans-serif",
              fontSize: isMobile ? '2.3rem' : '3.3rem',
              fontWeight: 850,
              letterSpacing: '-0.035em',
              color: '#0f172a',
              margin: '0 0 16px'
            }}
          >
            Who Can{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #170d2b 0%, #4F1985 50%, #9047ff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Participate?
            </span>
          </h2>

          <p style={{ fontSize: '1.08rem', color: '#64748b', lineHeight: 1.7, margin: 0 }}>
            Undergraduate and postgraduate students, PhD scholars, researchers, bioinformaticians, computational chemists, AI developers and anyone passionate about applying artificial intelligence to scientific research.
          </p>
        </div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-40px' }}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
            gap: '20px'
          }}
        >
          {participants.map((p, i) => (
            <motion.div
              key={i}
              variants={cardVariant}
              whileHover={{ y: -6 }}
              transition={{ type: 'spring', stiffness: 350, damping: 25 }}
            >
              <SpotlightCard
                style={{
                  padding: '28px 24px',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  justifyContent: 'space-between',
                  border: '1px solid rgba(15, 23, 42, 0.08)'
                }}
              >
                <div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '18px'
                    }}
                  >
                    <div
                      style={{
                        width: '46px',
                        height: '46px',
                        borderRadius: '14px',
                        backgroundColor: p.accentLight,
                        border: `1px solid ${p.accent}25`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {p.icon}
                    </div>

                    <span
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: '11px',
                        fontWeight: 800,
                        color: p.accent,
                        backgroundColor: `${p.accent}12`,
                        padding: '3px 10px',
                        borderRadius: '9999px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em'
                      }}
                    >
                      {p.category}
                    </span>
                  </div>

                  <h4
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: '1.25rem',
                      fontWeight: 800,
                      color: '#0f172a',
                      lineHeight: 1.3,
                      margin: '0 0 10px'
                    }}
                  >
                    {p.role}
                  </h4>

                  <p style={{ fontSize: '13.5px', color: '#475569', lineHeight: 1.6, margin: 0 }}>
                    {p.desc}
                  </p>
                </div>

                <div
                  style={{
                    marginTop: '20px',
                    paddingTop: '14px',
                    borderTop: '1px solid rgba(15, 23, 42, 0.06)',
                    fontSize: '11.5px',
                    fontWeight: 700,
                    color: '#64748b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: p.accent }} />
                  <span>{p.focus}</span>
                </div>
              </SpotlightCard>
            </motion.div>
          ))}
        </motion.div>
      </motion.section>

      <motion.section
        variants={sectionVariant}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-60px' }}
        style={{
          position: 'relative',
          padding: isMobile ? '56px 16px' : '88px 32px',
          maxWidth: '1360px',
          margin: '0 auto',
          zIndex: 1
        }}
      >
        <div style={{ textAlign: 'center', maxWidth: '820px', margin: '0 auto 44px' }}>
          <h2
            style={{
              fontFamily: "'Space Grotesk', -apple-system, sans-serif",
              fontSize: isMobile ? '2.3rem' : '3.3rem',
              fontWeight: 850,
              letterSpacing: '-0.035em',
              color: '#0f172a',
              margin: '0 0 14px'
            }}
          >
            Why{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #170d2b 0%, #4F1985 50%, #9047ff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Participate?
            </span>
          </h2>
        </div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-40px' }}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
            gap: '20px'
          }}
        >
          {whyParticipate.map((w, idx) => (
            <motion.div
              key={idx}
              variants={cardVariant}
              whileHover={{ y: -5 }}
              transition={{ type: 'spring', stiffness: 350, damping: 25 }}
            >
              <SpotlightCard
                style={{
                  padding: '32px 28px',
                  position: 'relative',
                  border: `1px solid rgba(15, 23, 42, 0.08)`,
                  boxShadow: '0 4px 20px -2px rgba(15, 23, 42, 0.04)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  height: '100%'
                }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                    <div
                      style={{
                        width: '46px',
                        height: '46px',
                        borderRadius: '14px',
                        backgroundColor: w.accentLight,
                        border: `1px solid ${w.accent}25`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 4px 12px -2px ${w.accent}15`
                      }}
                    >
                      {w.icon}
                    </div>

                    <span
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: '1.75rem',
                        fontWeight: 900,
                        color: `${w.accent}20`,
                        lineHeight: 1
                      }}
                    >
                      {w.num}
                    </span>
                  </div>

                  <h4
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: '1.3rem',
                      fontWeight: 800,
                      color: '#0f172a',
                      lineHeight: 1.3,
                      marginBottom: '10px'
                    }}
                  >
                    {w.title}
                  </h4>

                  <p style={{ fontSize: '14px', color: '#475569', lineHeight: 1.65, margin: 0 }}>
                    {w.desc}
                  </p>
                </div>

                <div
                  style={{
                    marginTop: '20px',
                    paddingTop: '14px',
                    borderTop: '1px solid rgba(15, 23, 42, 0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '11.5px',
                    fontWeight: 750,
                    color: w.accent
                  }}
                >
                  <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: w.accent }} />
                  <span>SciSprint 2026 Core Value</span>
                </div>
              </SpotlightCard>
            </motion.div>
          ))}
        </motion.div>
      </motion.section>

      <motion.section
        id="prizes"
        variants={sectionVariant}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-60px' }}
        style={{
          position: 'relative',
          padding: isMobile ? '56px 16px' : '88px 32px',
          maxWidth: '1280px',
          margin: '0 auto',
          zIndex: 1
        }}
      >
        <div style={{ textAlign: 'center', maxWidth: '820px', margin: '0 auto 44px' }}>
          <h2
            style={{
              fontFamily: "'Space Grotesk', -apple-system, sans-serif",
              fontSize: isMobile ? '2.3rem' : '3.3rem',
              fontWeight: 850,
              letterSpacing: '-0.035em',
              color: '#0f172a',
              margin: '0 0 14px'
            }}
          >
            Exciting Prizes &amp;{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #170d2b 0%, #4F1985 50%, #9047ff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Recognition
            </span>
          </h2>

          <p style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', margin: '0 auto 8px' }}>
            Get ready to compete, innovate and win!
          </p>

          <p style={{ fontSize: '1.05rem', color: '#64748b', lineHeight: 1.7, margin: 0 }}>
            SciSprint 2026 brings exciting prizes across all four hackathons, recognising outstanding ideas, innovative solutions and scientific excellence.
          </p>
        </div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-40px' }}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
            gap: '24px',
            marginBottom: '36px'
          }}
        >
          
          <motion.div
            variants={cardVariant}
            whileHover={{ y: -8 }}
            transition={{ type: 'spring', stiffness: 350, damping: 25 }}
          >
            <SpotlightCard
              style={{
                padding: '36px 28px',
                textAlign: 'center',
                border: '1.5px solid rgba(245, 158, 11, 0.35)',
                boxShadow: '0 12px 36px -6px rgba(245, 158, 11, 0.12), 0 2px 6px rgba(0,0,0,0.02)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%'
              }}
            >
              <div>
                <div
                  style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '20px',
                    backgroundColor: '#fffbeb',
                    border: '1.5px solid #fde68a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                    boxShadow: '0 8px 20px -4px rgba(245, 158, 11, 0.22)'
                  }}
                >
                  <Trophy size={30} color="#d97706" />
                </div>

                <div
                  style={{
                    display: 'inline-block',
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '11px',
                    fontWeight: 850,
                    color: '#b45309',
                    backgroundColor: 'rgba(245, 158, 11, 0.12)',
                    padding: '4px 12px',
                    borderRadius: '9999px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    marginBottom: '10px'
                  }}
                >
                  🏆 Exciting Prizes
                </div>

                <div
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: '2.6rem',
                    fontWeight: 900,
                    color: '#0f172a',
                    lineHeight: 1.1,
                    marginBottom: '12px'
                  }}
                >
                  INR 8,000
                </div>

                <p style={{ fontSize: '14px', color: '#475569', lineHeight: 1.65, margin: 0 }}>
                  Win exciting rewards worth INR 8,000 across the hackathon series recognising outstanding scientific innovations.
                </p>
              </div>

              <div
                style={{
                  marginTop: '24px',
                  paddingTop: '16px',
                  borderTop: '1px solid rgba(245, 158, 11, 0.2)',
                  fontSize: '11.5px',
                  fontWeight: 800,
                  color: '#b45309',
                  fontFamily: "'JetBrains Mono', monospace"
                }}
              >
                Awarded Across 4 Sprints
              </div>
            </SpotlightCard>
          </motion.div>

          <motion.div
            variants={cardVariant}
            whileHover={{ y: -8 }}
            transition={{ type: 'spring', stiffness: 350, damping: 25 }}
          >
            <SpotlightCard
              style={{
                padding: '36px 28px',
                textAlign: 'center',
                border: '1.5px solid rgba(79, 25, 133, 0.25)',
                boxShadow: '0 12px 36px -6px rgba(79, 25, 133, 0.1), 0 2px 6px rgba(0,0,0,0.02)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%'
              }}
            >
              <div>
                <div
                  style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '20px',
                    backgroundColor: '#faf8fe',
                    border: '1.5px solid rgba(79, 25, 133, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                    boxShadow: '0 8px 20px -4px rgba(79, 25, 133, 0.2)'
                  }}
                >
                  <ShieldCheck size={30} color="#4F1985" />
                </div>

                <div
                  style={{
                    display: 'inline-block',
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '11px',
                    fontWeight: 850,
                    color: '#4F1985',
                    backgroundColor: 'rgba(79, 25, 133, 0.1)',
                    padding: '4px 12px',
                    borderRadius: '9999px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    marginBottom: '10px'
                  }}
                >
                  🎓 Certificates for All
                </div>

                <div
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: '2.6rem',
                    fontWeight: 900,
                    color: '#0f172a',
                    lineHeight: 1.1,
                    marginBottom: '12px'
                  }}
                >
                  Guaranteed
                </div>

                <p style={{ fontSize: '14px', color: '#475569', lineHeight: 1.65, margin: 0 }}>
                  Every participant will receive an official certificate of participation from Boltzmann Labs and ChemBio AI.
                </p>
              </div>

              <div
                style={{
                  marginTop: '24px',
                  paddingTop: '16px',
                  borderTop: '1px solid rgba(79, 25, 133, 0.15)',
                  fontSize: '11.5px',
                  fontWeight: 800,
                  color: '#4F1985',
                  fontFamily: "'JetBrains Mono', monospace"
                }}
              >
                100% Verified Credentials
              </div>
            </SpotlightCard>
          </motion.div>

          <motion.div
            variants={cardVariant}
            whileHover={{ y: -8 }}
            transition={{ type: 'spring', stiffness: 350, damping: 25 }}
          >
            <SpotlightCard
              style={{
                padding: '36px 28px',
                textAlign: 'center',
                border: '1.5px solid rgba(2, 132, 199, 0.25)',
                boxShadow: '0 12px 36px -6px rgba(2, 132, 199, 0.1), 0 2px 6px rgba(0,0,0,0.02)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%'
              }}
            >
              <div>
                <div
                  style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '20px',
                    backgroundColor: '#f0f9ff',
                    border: '1.5px solid #bae6fd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                    boxShadow: '0 8px 20px -4px rgba(2, 132, 199, 0.2)'
                  }}
                >
                  <Star size={30} color="#0284c7" />
                </div>

                <div
                  style={{
                    display: 'inline-block',
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: '11px',
                    fontWeight: 850,
                    color: '#0284c7',
                    backgroundColor: 'rgba(2, 132, 199, 0.1)',
                    padding: '4px 12px',
                    borderRadius: '9999px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    marginBottom: '10px'
                  }}
                >
                  🌟 Recognition
                </div>

                <div
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: '2.6rem',
                    fontWeight: 900,
                    color: '#0f172a',
                    lineHeight: 1.1,
                    marginBottom: '12px'
                  }}
                >
                  Community
                </div>

                <p style={{ fontSize: '14px', color: '#475569', lineHeight: 1.65, margin: 0 }}>
                  Showcase your scientific innovations and gain visibility within the research community across academia and industry.
                </p>
              </div>

              <div
                style={{
                  marginTop: '24px',
                  paddingTop: '16px',
                  borderTop: '1px solid rgba(2, 132, 199, 0.15)',
                  fontSize: '11.5px',
                  fontWeight: 800,
                  color: '#0284c7',
                  fontFamily: "'JetBrains Mono', monospace"
                }}
              >
                Global Research Visibility
              </div>
            </SpotlightCard>
          </motion.div>
        </motion.div>

        <div
          style={{
            textAlign: 'center',
            padding: '18px 24px',
            borderRadius: '9999px',
            backgroundColor: '#ffffff',
            border: '1px solid rgba(15, 23, 42, 0.08)',
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: isMobile ? '13.5px' : '15.5px',
            fontWeight: 800,
            color: '#4F1985',
            letterSpacing: '0.04em'
          }}
        >
          Learn. Compete. Innovate. Get Recognised!
        </div>
      </motion.section>

      <motion.section
        variants={sectionVariant}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-60px' }}
        style={{
          position: 'relative',
          padding: isMobile ? '56px 16px' : '88px 32px',
          maxWidth: '1360px',
          margin: '0 auto',
          zIndex: 1
        }}
      >
        <SpotlightCard
          style={{
            padding: isMobile ? '36px 20px' : '56px 48px',
            textAlign: 'center',
            maxWidth: '1080px',
            margin: '0 auto',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '480px',
              height: '480px',
              background: 'radial-gradient(circle, rgba(88, 101, 242, 0.06) 0%, rgba(79, 25, 133, 0.04) 50%, transparent 75%)',
              filter: 'blur(50px)',
              pointerEvents: 'none'
            }}
          />

          <div style={{ position: 'relative', zIndex: 1, maxWidth: '820px', margin: '0 auto' }}>
            
            <div
              style={{
                marginBottom: '22px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <img
                src="/foundations/chembio-ai-logo-dark.png"
                alt="ChemBio AI Official Community Partner"
                style={{
                  height: isMobile ? '36px' : '44px',
                  maxWidth: '220px',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            </div>

            <h2
              style={{
                fontFamily: "'Space Grotesk', -apple-system, sans-serif",
                fontSize: isMobile ? '2.2rem' : '3.3rem',
                fontWeight: 850,
                letterSpacing: '-0.035em',
                color: '#0f172a',
                lineHeight: 1.15,
                margin: '0 0 18px'
              }}
            >
              Join the Scientific{' '}
              <span
                style={{
                  background: 'linear-gradient(135deg, #170d2b 0%, #4F1985 50%, #5865F2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                Revolution!
              </span>
            </h2>

            <p
              style={{
                fontSize: isMobile ? '15.5px' : '17.5px',
                color: '#475569',
                lineHeight: 1.7,
                marginBottom: '14px',
                fontWeight: 450
              }}
            >
              Be part of a growing community exploring the future of AI powered scientific discovery.
            </p>

            <p
              style={{
                fontSize: isMobile ? '14.5px' : '15.5px',
                color: '#334155',
                lineHeight: 1.65,
                marginBottom: '26px'
              }}
            >
              Register for SciSprint 2026 and join the <strong>ChemBio AI Discord community</strong> to participate, collaborate and stay updated on upcoming activities.
            </p>

            <div
              style={{
                display: 'inline-flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                padding: isMobile ? '10px 16px' : '10px 22px',
                borderRadius: '9999px',
                backgroundColor: '#faf8fe',
                border: '1px solid rgba(79, 25, 133, 0.12)',
                marginBottom: '34px',
                fontSize: '12.5px',
                color: '#4F1985',
                fontWeight: 750
              }}
            >
              <span>
                Powered by: <strong>Agent Scientist</strong>, developed by <strong>Boltzmann Labs</strong>
              </span>
              <span style={{ color: 'rgba(79, 25, 133, 0.35)' }}>|</span>
              <span style={{ color: '#5865F2' }}>
                Community Partner: <strong>ChemBio AI</strong>
              </span>
            </div>

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '16px'
              }}
            >
              
              <a
                href={overallCompleted ? "#completed" : "https://forms.gle/Px6ncuHhr4n4ZMJy8"}
                onClick={(e) => { if (overallCompleted) e.preventDefault(); }}
                target={overallCompleted ? undefined : "_blank"}
                rel="noopener noreferrer"
                className={overallCompleted ? undefined : "glossy-btn-primary glossy-btn-sheen"}
                style={{
                  padding: isMobile ? '14px 28px' : '16px 36px',
                  borderRadius: '9999px',
                  fontSize: '15px',
                  fontWeight: 750,
                  fontFamily: "'Space Grotesk', sans-serif",
                  textDecoration: 'none',
                  color: '#ffffff',
                  backgroundColor: overallCompleted ? '#94a3b8' : undefined,
                  cursor: overallCompleted ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  boxShadow: overallCompleted ? 'none' : '0 12px 30px -4px rgba(79, 25, 133, 0.42)',
                  opacity: overallCompleted ? 0.75 : 1
                }}
              >
                <span>{overallCompleted ? 'Event Completed' : 'Register for SciSprint 2026'}</span>
                {!overallCompleted && <ArrowRight size={16} />}
              </a>

              <a
                href="https://discord.gg/rXTkjn2HhH"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: isMobile ? '14px 26px' : '16px 32px',
                  borderRadius: '9999px',
                  backgroundColor: '#5865F2',
                  color: '#ffffff',
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: '15px',
                  fontWeight: 750,
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  boxShadow: '0 10px 28px -4px rgba(88, 101, 242, 0.45)',
                  transition: 'all 0.25s ease'
                }}
              >
                <MessageSquare size={17} />
                <span>Join ChemBio AI Discord Community</span>
                <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </SpotlightCard>
      </motion.section>

      <motion.section
        variants={sectionVariant}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-60px' }}
        style={{
          position: 'relative',
          padding: isMobile ? '56px 16px' : '88px 32px',
          maxWidth: '1180px',
          margin: '0 auto',
          zIndex: 1
        }}
      >
        <div style={{ textAlign: 'center', maxWidth: '820px', margin: '0 auto 44px' }}>
          <h2
            style={{
              fontFamily: "'Space Grotesk', -apple-system, sans-serif",
              fontSize: isMobile ? '2.3rem' : '3.3rem',
              fontWeight: 850,
              letterSpacing: '-0.035em',
              color: '#0f172a',
              margin: '0 0 14px'
            }}
          >
            Have Questions?{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #170d2b 0%, #4F1985 50%, #9047ff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Get in Touch!
            </span>
          </h2>

          <p style={{ fontSize: '1.08rem', color: '#64748b', lineHeight: 1.7, margin: 0 }}>
            Need help with registration, participation or hackathon challenges? We&apos;re happy to help! Reach out to our team for any queries related to SciSprint 2026.
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: '24px',
            marginBottom: '48px'
          }}
        >
          
          <motion.div
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 350, damping: 25 }}
          >
            <SpotlightCard style={{ padding: isMobile ? '28px 20px' : '36px 32px' }}>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '14px',
                  backgroundColor: 'rgba(79, 25, 133, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '18px'
                }}
              >
                <Mail size={22} color="#4F1985" />
              </div>

              <div
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '11px',
                  fontWeight: 800,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  marginBottom: '4px'
                }}
              >
                Email Us
              </div>

              <h4
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: isMobile ? '1.25rem' : '1.5rem',
                  fontWeight: 850,
                  color: '#0f172a',
                  marginBottom: '8px'
                }}
              >
                contact@chembioai.org
              </h4>

              <p style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.6, marginBottom: '24px', flex: 1 }}>
                Reach out to our team for any queries related to SciSprint 2026.
              </p>

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <a
                  href="mailto:contact@chembioai.org"
                  style={{
                    backgroundColor: '#4F1985',
                    color: '#ffffff',
                    padding: '12px 24px',
                    borderRadius: '9999px',
                    fontSize: '13.5px',
                    fontWeight: 750,
                    fontFamily: "'Space Grotesk', sans-serif",
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Mail size={15} />
                  <span>Send Email</span>
                </a>

                <button
                  type="button"
                  onClick={handleCopyEmail}
                  style={{
                    backgroundColor: '#faf8fe',
                    color: '#4F1985',
                    border: '1px solid rgba(79, 25, 133, 0.2)',
                    padding: '12px 18px',
                    borderRadius: '9999px',
                    fontSize: '13px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {copiedEmail ? <CheckCircle2 size={15} color="#10b981" /> : <Copy size={15} />}
                  <span>{copiedEmail ? 'Copied' : 'Copy'}</span>
                </button>
              </div>
            </SpotlightCard>
          </motion.div>

          <motion.div
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 350, damping: 25 }}
          >
            <SpotlightCard style={{ padding: isMobile ? '28px 20px' : '36px 32px' }}>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '14px',
                  backgroundColor: 'rgba(16, 185, 129, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '18px'
                }}
              >
                <Phone size={22} color="#10b981" />
              </div>

              <div
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '11px',
                  fontWeight: 800,
                  color: '#64748b',
                  textTransform: 'uppercase',
                  marginBottom: '4px'
                }}
              >
                WhatsApp
              </div>

              <h4
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: isMobile ? '1.25rem' : '1.5rem',
                  fontWeight: 850,
                  color: '#0f172a',
                  marginBottom: '8px'
                }}
              >
                +91 89198 20691
              </h4>

              <p style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.6, marginBottom: '24px', flex: 1 }}>
                Quick message our team directly for registration or participation help.
              </p>

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <a
                  href="http://wa.me/918919820691"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    backgroundColor: '#10b981',
                    color: '#ffffff',
                    padding: '12px 24px',
                    borderRadius: '9999px',
                    fontSize: '13.5px',
                    fontWeight: 750,
                    fontFamily: "'Space Grotesk', sans-serif",
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 16px rgba(16, 185, 129, 0.25)'
                  }}
                >
                  <Phone size={15} />
                  <span>Open WhatsApp</span>
                </a>

                <button
                  type="button"
                  onClick={handleCopyPhone}
                  style={{
                    backgroundColor: '#f0fdf4',
                    color: '#059669',
                    border: '1px solid rgba(16, 185, 129, 0.25)',
                    padding: '12px 18px',
                    borderRadius: '9999px',
                    fontSize: '13px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {copiedPhone ? <CheckCircle2 size={15} color="#10b981" /> : <Copy size={15} />}
                  <span>{copiedPhone ? 'Copied' : 'Copy'}</span>
                </button>
              </div>
            </SpotlightCard>
          </motion.div>
        </div>

        <div style={{ textAlign: 'center' }}>
          <h3
            style={{
              fontFamily: "'Space Grotesk', -apple-system, sans-serif",
              fontSize: 'clamp(1.8rem, 3.8vw, 2.8rem)',
              fontWeight: 850,
              color: '#0f172a',
              letterSpacing: '-0.03em',
              margin: '0 0 16px'
            }}
          >
            We look forward to seeing you at{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #170d2b 0%, #4F1985 50%, #9047ff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              SciSprint 2026!
            </span>
          </h3>

          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '13px',
              fontWeight: 850,
              color: '#4F1985',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              marginBottom: '28px'
            }}
          >
            NEW ERA. NEW EXPERIMENTS. NEW POSSIBILITIES.
          </div>

          <a
            href="https://forms.gle/Px6ncuHhr4n4ZMJy8"
            target="_blank"
            rel="noopener noreferrer"
            className="glossy-btn-primary glossy-btn-sheen"
            style={{
              padding: '16px 40px',
              borderRadius: '9999px',
              fontSize: '15.5px',
              fontWeight: 750,
              fontFamily: "'Space Grotesk', sans-serif",
              textDecoration: 'none',
              color: '#ffffff',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px'
            }}
          >
            <span>Register for SciSprint 2026</span>
            <ArrowRight size={17} />
          </a>
        </div>
      </motion.section>

      <AnimatePresence>
        {showPosterModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.88)',
              backdropFilter: 'blur(12px)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: isMobile ? '16px' : '40px'
            }}
            onClick={() => setShowPosterModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={{
                position: 'relative',
                maxWidth: '1200px',
                width: '100%',
                backgroundColor: '#0c0717',
                borderRadius: '24px',
                overflow: 'hidden',
                boxShadow: '0 30px 80px rgba(0, 0, 0, 0.6)',
                border: '1px solid rgba(255, 255, 255, 0.15)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '14px 20px',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                  color: '#ffffff'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Sparkles size={16} color="#c084fc" />
                  <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 750, fontSize: '14px' }}>
                    SciSprint 2026 &bull; Official Key Visual (1920&times;1080)
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <a
                    href="/scisprint2026_poster.png"
                    download="SciSprint_2026_Poster.png"
                    style={{
                      padding: '6px 14px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: '#ffffff',
                      textDecoration: 'none',
                      fontSize: '12px',
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Download size={13} />
                    <span>Download</span>
                  </a>

                  <button
                    type="button"
                    onClick={() => setShowPosterModal(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ffffff',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div style={{ maxHeight: '80vh', overflow: 'auto' }}>
                <img
                  src="/scisprint2026_poster.png"
                  alt="SciSprint 2026 Official Master Poster"
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}

